import { Box, Button, Text, VStack } from "@chakra-ui/react";
import { MathJax } from "better-react-mathjax";
import { CalculationArithmeticList } from "features/calculation/CalculationArithmeticList";
import { useTransactionCalculationAndFeeBasisListSuspense } from "gen/endpoints/transactions/transactions";
import { GenericTableRowMetadata } from "gen/model/genericTableRowMetadata";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { TransactionCalculationRowMetadata } from "gen/model/transactionCalculationRowMetadata";
import { noop } from "lodash-es";
import { parse as mathParse } from "mathjs";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { Restricted } from "shared/components/access/Restricted";
import { RouteLink } from "shared/components/link/RouteLink";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxPanel } from "shared/components/table/box/TableBoxPanel";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetLinkInfoForRowFn } from "shared/components/table/data/DataTableTypes";
import { ArrowUpRightIcon } from "shared/icons/ArrowUpRightIcon";
import { rootRoute } from "shared/navigation/Routes";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { StringParam, useQueryParam } from "use-query-params";

export const CalculationListTable: React.FC<{
  fundFamilyId: string;
  onClickAddCalculation: () => void;
}> = ({ fundFamilyId, onClickAddCalculation }) => {
  const { t } = useTranslation(["common", "calculation"]);

  const [selectedCalculationId, setSelectedCalculationId] = useQueryParam(
    "calculationId",
    StringParam,
  );

  const { data } =
    useTransactionCalculationAndFeeBasisListSuspense(fundFamilyId);

  const getLinkInfoForRow = useCallback<GetLinkInfoForRowFn>(
    ({ row }) => ({
      type: "onclick",
      onClick: () => {
        if (isTransactionCalculationRowMetadata(row.data?.metadata)) {
          setSelectedCalculationId(row.data.metadata.definition.id);
        }
      },
    }),
    [setSelectedCalculationId],
  );

  return (
    <TableBox isFullWidth={true} isFullHeight={true}>
      <TableBoxHeader>
        <TableBoxTitle>{t("calculation:calculations")}</TableBoxTitle>
        <DataTableBoxActions>
          <Restricted permission={PermissionTypeEnum.config}>
            <Button size="xs" onClick={onClickAddCalculation}>
              {t("calculation:addCustomCalculation")}
            </Button>
          </Restricted>
        </DataTableBoxActions>
      </TableBoxHeader>
      <TableBoxBody>
        <DataTable
          data={data}
          rowSelectionType="single"
          rowSelection={
            selectedCalculationId
              ? { [selectedCalculationId]: true }
              : undefined
          }
          rowSelectionCheckboxes={false}
          onRowSelectionChange={noop}
          getLinkInfoForRow={getLinkInfoForRow}
        />
        {selectedCalculationId && (
          <QueryBoundary>
            <CalculationPanel
              fundFamilyId={fundFamilyId}
              calculationId={selectedCalculationId}
              onClose={() => setSelectedCalculationId(undefined)}
            />
          </QueryBoundary>
        )}
      </TableBoxBody>
    </TableBox>
  );
};

interface CalculationPanelProps {
  fundFamilyId: string;
  calculationId: string;
  onClose: () => void;
}

const CalculationPanel = ({
  fundFamilyId,
  calculationId,
  onClose,
}: CalculationPanelProps) => {
  const { t } = useTranslation(["calculation", "common"]);

  const { data } = useTransactionCalculationAndFeeBasisListSuspense(
    fundFamilyId,
    {
      query: {
        select: (data) =>
          (
            data.rows.find(
              (row) =>
                isTransactionCalculationRowMetadata(row.metadata) &&
                row.metadata.definition.id === calculationId,
            )?.metadata as TransactionCalculationRowMetadata
          ).definition,
      },
    },
  );

  return (
    <TableBoxPanel onClose={onClose}>
      <VStack p="24px" spacing="16px" alignItems="flex-start">
        <Text textStyle="h6">{data.name}</Text>
        <Restricted permission={PermissionTypeEnum.config}>
          <RouteLink
            to={
              data.id.startsWith("TRCY_")
                ? rootRoute({})
                    .fundFamilies({})
                    .fundFamily({ fundFamilyId })
                    .setup({})
                    .fund({})
                    .calculationEdit({
                      transactionCalculationId: data.id,
                    }).$
                : rootRoute({})
                    .fundFamilies({})
                    .fundFamily({ fundFamilyId })
                    .setup({})
                    .fund({})
                    .basisEdit({
                      feeBasisId: data.id,
                    }).$
            }
          >
            {t("calculation:editCalculation")}
            <ArrowUpRightIcon />
          </RouteLink>
        </Restricted>
        {data.calculation_definitions.map((def, index) => (
          <Box key={index} borderTopWidth="1px" w="100%" py="8px">
            {data.time_periods > 1
              ? [def.start_date, def.end_date]
                  .map(
                    (date) =>
                      formatIsoDateToDisplay(date) || t("common:infinity"),
                  )
                  .join(" - ")
              : undefined}
            <Box mx="16px" mt="20px" overflowX="auto">
              {def.formula.type === "basic" && (
                <CalculationArithmeticList>
                  {def.formula.transaction_codes.map((op) => (
                    <CalculationArithmeticList.Item
                      key={op.code_name}
                      addOrSubtract={op.add_or_subtract}
                    >
                      {op.code_name}
                    </CalculationArithmeticList.Item>
                  ))}
                </CalculationArithmeticList>
              )}
              {def.formula.type === "advanced" && (
                <Box
                  display="inline-block"
                  w="100%"
                  sx={{
                    svg: {
                      w: "100%",
                    },
                  }}
                >
                  <MathJax dynamic={true}>{`$$${mathParse(
                    def.formula.eval_string,
                  ).toTex()}$$`}</MathJax>
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </VStack>
    </TableBoxPanel>
  );
};

const isTransactionCalculationRowMetadata = (
  metadata: GenericTableRowMetadata | undefined | null,
): metadata is TransactionCalculationRowMetadata =>
  metadata?.type === "transaction_calculation";
